/**
 * Genre "starts with" helpers for Luminate dashboard filters.
 * Prefer known Focus Genres prefixes (longest first); otherwise first comma segment.
 */

export const LUMINATE_FOCUS_GENRE_PREFIXES = [
  'Indie Rock',
  'Afrobeats',
  'Alternative',
  'Country',
  'Folk',
  'Latin',
  'Pop',
  'Punk',
  'R&B',
  'Rap',
  'Rock',
]

const _PREFIXES_LONGEST_FIRST = [...LUMINATE_FOCUS_GENRE_PREFIXES].sort(
  (a, b) => b.length - a.length,
)

function _blank(value) {
  if (value == null) return true
  const s = String(value).trim()
  return !s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none'
}

/**
 * Canonical starts-with prefix for a Luminate Genres cell.
 * @returns {string | null}
 */
export function luminateGenreStartsWithPrefix(genre) {
  if (_blank(genre)) return null
  const raw = String(genre).trim()
  const low = raw.toLowerCase()
  for (const prefix of _PREFIXES_LONGEST_FIRST) {
    if (low.startsWith(prefix.toLowerCase())) return prefix
  }
  const lead = raw.split(',')[0].trim()
  return lead || null
}

/**
 * Distinct genre-prefix options present in ``rows`` (sorted).
 */
export function genreStartsWithOptionsFromRows(rows, column = 'Luminate Genres') {
  if (!Array.isArray(rows) || !rows.length) return []
  const seen = new Set()
  for (const row of rows) {
    const prefix = luminateGenreStartsWithPrefix(row?.[column])
    if (prefix) seen.add(prefix)
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

/**
 * Distinct exact values for a column (e.g. Country of Origin).
 */
export function distinctColumnOptionsFromRows(rows, column) {
  if (!Array.isArray(rows) || !rows.length || !column) return []
  const seen = new Set()
  for (const row of rows) {
    const raw = row?.[column]
    if (_blank(raw)) continue
    seen.add(String(raw).trim())
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

/** Row matches if genre starts with any selected prefix (OR). Empty selection → all pass. */
export function rowMatchesGenreStartsWith(row, column, selectedPrefixes) {
  if (!selectedPrefixes?.length) return true
  const genre = String(row?.[column] ?? '').trim().toLowerCase()
  if (!genre || genre === 'nan' || genre === 'none') return false
  return selectedPrefixes.some((p) => genre.startsWith(String(p).toLowerCase()))
}

/** Row matches if column equals any selected value (case-insensitive OR). */
export function rowMatchesExactColumn(row, column, selectedValues) {
  if (!selectedValues?.length) return true
  const val = String(row?.[column] ?? '').trim().toLowerCase()
  if (!val || val === 'nan' || val === 'none') return false
  return selectedValues.some((v) => val === String(v).trim().toLowerCase())
}
